import Vue from 'vue';
import { handleError, initClient } from '../../api';
import { fixISettings, WindowType } from '../../util';
import {
  AccountsAPIControllerUIClient,
  AccountSelect,
  ApplicationConfigControllerUIClient,
  ApprovalsApiV2UiClient,
  AppUiConfigResponse,
  AttachmentsAPIControllerUIClient,
  AttributeDefinitionsAPIControllerUIClient,
  Board,
  BoardsAPIControllerUIClient,
  BoardSharedUserInfo,
  Card,
  CardsAPIControllerUIClient,
  CardSharedUserInfo,
  CommentRepliesAPIControllerUIClient,
  CommentsAPIControllerUIClient,
  KanbanSettings,
  Label,
  LabelsAPIControllerUIClient,
  ListItem,
  LoggingAPIControllerUIClient,
  LogItemResultDto,
  PortAPIControllerUIClient,
  PrintAPIControllerUIClient,
  SettingsApiControllerUIClient,
  TaskItemsAPIControllerUIClient,
  TemplatesApiV2UiClient,
  TicketsAPIControllerUIClient,
  TimeTracker,
  TimeTrackingApiV2UiClient,
  UserBoardsAPIControllerUIClient,
  UserCardsAPIControllerUIClient,
  UsersAPIControllerUIClient,
  WorkflowApproval,
} from '../backend/backendapi';
import { Toasted } from 'vue-toasted';
import VueI18n from 'vue-i18n';

declare var window: WindowType;

fixISettings();

export class KanbanState {
    //data
    accounts: AccountSelect | null = null;
    readonly boards: Board[] = [];
    readonly templates: Board[] = [];
    readonly labels: Label[] = [];
    readonly openApprovals: WorkflowApproval[] = [];
    readonly shareBoardInfos: BoardSharedUserInfo[] = [];
    readonly shareCardInfos: CardSharedUserInfo[] = [];
    readonly timeTrackers: TimeTracker[] = [];
    readonly users: ListItem[] = [];
    history: LogItemResultDto | null = null;
    settings: KanbanSettings | null = null;
    config: AppUiConfigResponse | null = null;

    //loading state
    updates: number = 0;
    loading: boolean = false;
    loadedOnce: boolean = false;

    //settings
    // layout: 'list' | 'trello' = 'list';
    get layoutAttr() { return 'data-' + this.settings?.layout; }
    archived: boolean = false;
    templatesView: boolean = false;
    // alwaysShowMyBoards: boolean = false;
    filter: (c: Card) => boolean = (c: Card) => true;
    selectedViaLog: SelectedItemDto | null = null;
    historyDays: number = 30;
    private selectedBoardId: number | null = null;

    get selectedBoard(): Board | null {
        return this.boards.find(e => e.id === this.selectedBoardId) ?? null;
    }

    set selectedBoard(val: Board | null) {
        this.selectedBoardId = val ? val.id! : null;
    };

    //mass modify
    massSelectionEnabled: boolean = false;
    readonly massSelectionCards: Card[] = [];

    //apis
    readonly applicationConfigApi = initClient(ApplicationConfigControllerUIClient);
    readonly approvalAPI = initClient(ApprovalsApiV2UiClient);
    readonly attachmentAPI = initClient(AttachmentsAPIControllerUIClient);
    readonly attributeDefinitionAPI = initClient(AttributeDefinitionsAPIControllerUIClient);
    readonly accountsAPI = initClient(AccountsAPIControllerUIClient);
    readonly boardAPI = initClient(BoardsAPIControllerUIClient);
    readonly cardsAPI = initClient(CardsAPIControllerUIClient);
    readonly commentAPI = initClient(CommentsAPIControllerUIClient);
    readonly labelAPI = initClient(LabelsAPIControllerUIClient);
    readonly logsApi = initClient(LoggingAPIControllerUIClient);
    readonly portApi = initClient(PortAPIControllerUIClient);
    readonly printApi = initClient(PrintAPIControllerUIClient);
    readonly replyAPI = initClient(CommentRepliesAPIControllerUIClient);
    readonly sharedBoardAPI = initClient(UserBoardsAPIControllerUIClient);
    readonly sharedCardAPI = initClient(UserCardsAPIControllerUIClient);
    readonly settingsAPI = initClient(SettingsApiControllerUIClient);
    readonly taskAPI = initClient(TaskItemsAPIControllerUIClient);
    readonly templatesApi = initClient(TemplatesApiV2UiClient);
    readonly ticketAPI = initClient(TicketsAPIControllerUIClient);
    readonly timeTrackingApi = initClient(TimeTrackingApiV2UiClient);
    readonly userAPI = initClient(UsersAPIControllerUIClient);

    //permissions
    get canDeleteCards(): boolean {
        return window.application.permissions.kanban.cardDelete;
    }

    get canDeleteFlows(): boolean {
        return window.application.permissions.kanban.flowDelete;
    }

    get canDeleteBoards(): boolean {
        return window.application.permissions.kanban.boardDelete;
    }

    get canEditFlows(): boolean {
        return window.application.permissions.kanban.flowEdit;
    }

    get canEditBoards(): boolean {
        return window.application.permissions.kanban.boardEdit;
    }

    //data loading
    async reloadAll(vue: Vue): Promise<void> {
        //reset some things
        this.massSelectionEnabled = false;
        this.massSelectionCards.splice(0);

        //load
        this.updates = 0;
        this.loading = true;
        await this.reloadSettings(vue.$toasted, vue.$i18n);//settings first as board loading depends on it
        await Promise.all([
            this.reloadBoards(vue.$toasted, vue.$i18n),
            this.reloadTemplates(vue.$toasted, vue.$i18n),
            this.reloadBoardShareInfo(vue.$toasted, vue.$i18n),
            this.reloadCardShareInfo(vue.$toasted, vue.$i18n),
            this.reloadLabels(vue.$toasted, vue.$i18n),
            this.reloadUsers(vue.$toasted, vue.$i18n),
            this.reloadAccounts(vue.$toasted, vue.$i18n),
            this.reloadHistory(vue.$toasted, vue.$i18n),
            this.reloadTimeTrackers(vue.$toasted, vue.$i18n),
            this.reloadOpenApprovals(vue.$toasted, vue.$i18n),
            this.reloadApplicationConfig(vue.$toasted, vue.$i18n),
        ]);
        if (this.loadedOnce) { //so the reloaded message does only come up on reload and not first load
            vue.$toasted.info(vue.$t('kanbanReloaded').toString());
        }
        this.loadedOnce = true;

        this.loading = false;
    }

    private async reloadBoards($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            const boards = (await this.boardAPI.list(this.archived, this.settings!.alwaysShowMyBoards)).result!;
            console.log(boards);
            this.boards.splice(0);
            this.boards.push(...boards);
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    private async reloadTemplates($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            const templates = await this.templatesApi.templates();
            console.log(templates);
            this.templates.splice(0);
            this.templates.push(...templates);
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    private async reloadBoardShareInfo($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            const shareInfos = (await this.sharedBoardAPI.list()).result!;
            console.log(shareInfos);
            this.shareBoardInfos.splice(0);
            this.shareBoardInfos.push(...shareInfos);
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    private async reloadCardShareInfo($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            const shareInfos = (await this.sharedCardAPI.list(this.archived)).result!;
            console.log(shareInfos);
            this.shareCardInfos.splice(0);
            this.shareCardInfos.push(...shareInfos);
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    private async reloadLabels($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            const labels = (await this.labelAPI.list()).result!;
            console.log(labels);
            this.labels.splice(0);
            this.labels.push(...labels);
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    private async reloadUsers($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            const users = (await this.userAPI.list()).result!;
            console.log(users);
            this.users.splice(0);
            this.users.push(...users);
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    private async reloadAccounts($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            this.accounts = (await this.accountsAPI.list()).result!;
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    private async reloadSettings($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            const set = (await this.settingsAPI.get("KanbanSettings", false, false)).result!;
            this.settings = new KanbanSettings();
            this.settings.init(set);
            //existing settings have no value here, so use 'list' as default
            this.settings.layout ??= 'list';
            this.settings.alwaysShowMyBoards ??= false;
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    async reloadHistory($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            this.history = (await this.logsApi.getEvents(this.historyDays)).result!;
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    async reloadTimeTrackers($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            const trackers = await this.timeTrackingApi.list();
            this.timeTrackers.splice(0);
            this.timeTrackers.push(...trackers);
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }
    
    async reloadOpenApprovals($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            const openApprovals = await this.approvalAPI.openMine();
            this.openApprovals.splice(0);
            this.openApprovals.push(...openApprovals);
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }

    async reloadApplicationConfig($toasted: Toasted, $i18n: VueI18n): Promise<void> {
        try {
            this.config = await this.applicationConfigApi.getAppUiConfig();
        } catch (ex) {
            handleError($toasted, $i18n, ex);
        }
    }
}

export class SelectedItemDto {
    public boardId: number | null = null;
    public flowId: number | null = null;
    public cardId: string | null = null;
}